import { Link } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { HardHat, Building2, Users, TrendingUp, Clock, Globe, Network, ArrowRight } from 'lucide-react';

const categories = [
  {
    id: 'tecnici',
    icon: HardHat,
    title: 'Tecnici e Figure Manageriali',
    description: 'Site Supervisor, Project Manager, HSE Manager, Commissioning Engineer e altre figure tecniche specializzate.',
    cta: 'Candidati',
    link: '/lavora-con-noi/tecnici',
    color: '#27AE60',
    image: 'https://images.unsplash.com/photo-1667344970482-e939871e9f25?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwZW5naW5lZXIlMjB0YWJsZXQlMjBjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3MjIwMjc5M3ww&ixlib=rb-4.1.0&q=85&w=400'
  },
  {
    id: 'imprese',
    icon: Building2,
    title: 'Imprese Subappaltatrici',
    description: 'Carpenteria, montaggi meccanici, impianti elettrici, saldature certificate e manutenzione industriale.',
    cta: 'Candidati',
    link: '/lavora-con-noi/imprese',
    color: '#E94560',
    image: 'https://images.unsplash.com/photo-1638262052640-82e94d64664a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGhhbmRzaGFrZXxlbnwwfHx8fDE3NzIyMDI4MDF8MA&ixlib=rb-4.1.0&q=85&w=400'
  },
  {
    id: 'freelance',
    icon: Users,
    title: 'Collaboratori Freelance',
    description: 'Business Developer e Recruiter freelance per espandere il nostro network e creare nuove opportunità.',
    cta: 'Scopri il modello',
    link: '/lavora-con-noi/freelance',
    color: '#1A1A2E',
    image: 'https://images.unsplash.com/photo-1646579886741-12b59840c63f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwZW5naW5lZXJpbmclMjB0ZWFtJTIwbWVldGluZ3xlbnwwfHx8fDE3NzIyMDI4MDJ8MA&ixlib=rb-4.1.0&q=85&w=400'
  }
];

const benefits = [
  { icon: Clock, title: 'Flessibilità', description: 'Lavora quando vuoi, dove vuoi' },
  { icon: TrendingUp, title: 'Guadagno', description: 'Compensi competitivi e trasparenti' },
  { icon: Globe, title: 'Crescita', description: 'Opportunità di sviluppo professionale' },
  { icon: Network, title: 'Network', description: 'Accesso a progetti esclusivi' }
];

export default function LavoraConNoiPage() {
  return (
    <div data-testid="lavora-con-noi-page">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="hero-dark py-20 md:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="accent-line mb-8"></div>
            <h1 data-testid="lavora-headline" className="text-4xl md:text-6xl font-extrabold text-white mb-6">
              Lavora con Noi
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl">
              Unisciti al nostro network di professionisti e imprese. 
              Offriamo opportunità concrete per chi vuole crescere nel settore EPC e industriale.
            </p>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-20 md:py-32 bg-[#F5F5F7] noise-bg relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {categories.map((cat) => (
                <Link 
                  key={cat.id}
                  to={cat.link}
                  data-testid={`category-card-${cat.id}`}
                  className="card-industrial group overflow-hidden flex flex-col"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={cat.image} 
                      alt={cat.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A2E] to-transparent opacity-70"></div>
                    <div className="absolute bottom-4 left-4">
                      <cat.icon className="text-white w-12 h-12" strokeWidth={1.5} />
                    </div>
                  </div>
                  <div className="p-8 flex-1 flex flex-col">
                    <h3 className="text-xl font-bold text-[#1A1A2E] mb-3 group-hover:text-[#E94560] transition-colors">
                      {cat.title}
                    </h3>
                    <p className="text-[#555555] mb-6 flex-1">
                      {cat.description}
                    </p>
                    <span 
                      className="inline-flex items-center gap-2 font-bold"
                      style={{ color: cat.color }}
                    >
                      {cat.cta}
                      <ArrowRight size={18} className="transition-transform group-hover:translate-x-2" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-xs font-bold uppercase tracking-widest text-[#E94560] mb-4 block">
                I Vantaggi
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-[#1A1A2E]">
                Perché collaborare con Sell&Rec
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {benefits.map((benefit, i) => (
                <div key={i} data-testid={`benefit-${i}`} className="text-center">
                  <benefit.icon className="w-12 h-12 mx-auto mb-4 text-[#27AE60]" strokeWidth={1.5} />
                  <h3 className="font-bold text-[#1A1A2E] mb-2">{benefit.title}</h3>
                  <p className="text-sm text-[#888888]">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
